import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useVerifyBenefits } from '@/hooks/use-benefits'
import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/stores/auth.store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

const schema = z.object({
  first_name: z.string().min(1, 'Required'),
  last_name: z.string().min(1, 'Required'),
  address: z.string().min(1, 'Required'),
  employee_id: z.string().min(1, 'Required'),
  password: z.string().min(8, 'At least 8 characters'),
})

type FormData = z.infer<typeof schema>

export function BenefitsSignupPage() {
  const [params] = useSearchParams()
  const orgSlug = params.get('org') ?? ''
  const navigate = useNavigate()
  const verify = useVerifyBenefits()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: FormData) => {
    setError(null)
    try {
      const { access_token } = await verify.mutateAsync({
        ...data,
        organization_slug: orgSlug,
      })
      const user = await authService.getCurrentUser()
      setAuth(user, access_token)
      navigate('/benefits/election')
    } catch {
      setError("We couldn't verify your details. Contact your HR team.")
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-lighter/40 px-4 py-12">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-2 border-t-4 border-t-brand pb-6">
          <CardTitle className="text-center text-2xl font-bold">
            Set up your benefits account
          </CardTitle>
          <CardDescription className="text-center text-base">
            Verify your identity to choose your severance benefits.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First name</Label>
              <Input
                id="first_name"
                type="text"
                placeholder="Jane"
                {...register('first_name')}
                aria-invalid={errors.first_name ? 'true' : 'false'}
                disabled={verify.isPending}
              />
              {errors.first_name && (
                <p className="text-sm text-destructive">{errors.first_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last name</Label>
              <Input
                id="last_name"
                type="text"
                placeholder="Doe"
                {...register('last_name')}
                aria-invalid={errors.last_name ? 'true' : 'false'}
                disabled={verify.isPending}
              />
              {errors.last_name && (
                <p className="text-sm text-destructive">{errors.last_name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                type="text"
                placeholder="123 Main St"
                {...register('address')}
                aria-invalid={errors.address ? 'true' : 'false'}
                disabled={verify.isPending}
              />
              {errors.address && (
                <p className="text-sm text-destructive">{errors.address.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="employee_id">Work email or employee ID</Label>
              <Input
                id="employee_id"
                type="text"
                placeholder="you@company.com"
                {...register('employee_id')}
                aria-invalid={errors.employee_id ? 'true' : 'false'}
                disabled={verify.isPending}
              />
              {errors.employee_id && (
                <p className="text-sm text-destructive">{errors.employee_id.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                {...register('password')}
                aria-invalid={errors.password ? 'true' : 'false'}
                disabled={verify.isPending}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" className="w-full" disabled={verify.isPending}>
              Continue
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
